// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  useBrokersServiceBrokersBrokersProfilePartialUpdate,
  useBrokersServiceBrokersBrokersProfileRetrieve,
  useBrokersServiceBrokersBrokersProfileRetrieveKey
} from '@insurance/api/queries'
import { WizardContext, WizardContextType } from '@shared/context'
import { useSessionContext } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { showApiErrors } from '@shared/utils'
import { useQueryClient } from '@tanstack/react-query'
import { TourProps } from 'antd'
import { AnyObject } from 'antd/es/_util/type'
import { PropsWithChildren, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

export const WizardProvider = ({ children }: PropsWithChildren) => {
  const refStepOne = useRef<HTMLDivElement>(null)
  const refStepTwo = useRef<HTMLAnchorElement>(null)
  const refStepThree = useRef<HTMLAnchorElement>(null)
  const refStepFour = useRef<HTMLDivElement>(null)
  const refStepFive = useRef<HTMLDivElement>(null)
  const refStepSix = useRef<HTMLAnchorElement>(null)
  const queryClient = useQueryClient()

  const { broker } = useSessionContext()

  const isBrokerAdmin = !!broker?.user?.is_broker_admin

  const { data, isLoading } = useBrokersServiceBrokersBrokersProfileRetrieve(
    undefined,
    {
      enabled: isBrokerAdmin
    }
  )

  const { mutate: mutateEdit, isPending } =
    useBrokersServiceBrokersBrokersProfilePartialUpdate({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [useBrokersServiceBrokersBrokersProfileRetrieveKey]
        })
      },
      onError: (error) => showApiErrors(error)
    })

  useEffect(() => {
    if (data?.setup !== undefined && data?.setup < 5) {
      setOpen(true)
    } else {
      setOpen(false)
    }
  }, [data])

  const { pathname } = useLocation()
  const { t } = useLocales()
  const [open, setOpen] = useState(false)

  const isLoadingActions = isLoading || isPending

  const steps: TourProps['steps'] = [
    {
      title: t('welcome_wizard'),
      description: t('welcome_wizard_description'),
      nextButtonProps: {
        children: t('start'),
        onClick: () => onChangeStep(1),
        loading: isLoadingActions
      } as AnyObject,
      target: () => refStepOne.current!
    },
    {
      title: t('personal_page'),
      description: t('personal_page_description'),
      onPrev: () => onChangeStep(0),
      nextButtonProps: {
        loading: isLoadingActions,
        disabled: !pathname.includes('/staff'),
        onClick: () => onChangeStep(2)
      } as AnyObject,
      placement: 'right',
      target: () => refStepTwo.current!
    },
    {
      title: t('add_agent'),
      description: t('add_agent_description'),
      placement: 'left',
      prevButtonProps: {
        loading: isLoadingActions,
        onClick: () => onChangeStep(1)
      } as AnyObject,
      nextButtonProps: {
        loading: isLoadingActions,
        style: {
          display: 'none'
        }
      } as AnyObject,
      target: () => refStepThree.current!
    },
    {
      title: t('teams_page'),
      description: t('teams_page_description'),
      prevButtonProps: {
        loading: isLoadingActions,
        style: {
          display: 'none'
        }
      } as AnyObject,
      nextButtonProps: {
        loading: isLoadingActions,
        onClick: () => onChangeStep(4),
        disabled: !pathname.includes('/staff/teams')
      } as AnyObject,
      target: () => refStepFive.current!
    },
    {
      title: t('add_team'),
      description: t('add_team_description'),
      placement: 'left',
      prevButtonProps: {
        loading: isLoadingActions,
        onClick: () => onChangeStep(3)
      } as AnyObject,
      nextButtonProps: {
        loading: isLoadingActions,
        onClick: () => onChangeStep(5)
      } as AnyObject,
      target: () => refStepSix.current!
    }
  ]

  const onChangeStep = (step: number) => {
    if (!isBrokerAdmin) return

    mutateEdit({ requestBody: { setup: step } })
  }

  const value: WizardContextType = {
    refStepOne,
    refStepTwo,
    refStepThree,
    refStepFour,
    refStepFive,
    refStepSix,
    open,
    steps,
    current: data?.setup || 0,
    isLoading: isLoadingActions,
    isBrokerAdmin,
    setOpen: isBrokerAdmin ? setOpen : () => null,
    onChangeStep
  }

  return (
    <WizardContext.Provider value={value}>{children}</WizardContext.Provider>
  )
}
